import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { HttpService } from './http.service';
import { catchError, Observable, of } from 'rxjs';
import {
  AddressResponse,
  CustomerAddressResponse,
  CustomerResponse,
  OneCustomerAddressResponse,
  SubmitResponse,
  WishlistByCategoryResponse,
  WishlistCategoriesResponse,
  WishlistResponse
} from '../../models';
import { ChangePassword, CreateCustomerAddressInput, MoveToWishlistCategoryInput, UpdateCustomerAddressInput, UpdateCustomerInput } from '../../inputs';

@Injectable({
  providedIn: 'root'
})
export class CustomerService extends HttpService<
  CreateCustomerAddressInput | UpdateCustomerAddressInput | UpdateCustomerInput | ChangePassword | MoveToWishlistCategoryInput,
  CustomerAddressResponse | OneCustomerAddressResponse
> {
  constructor(httpClient: HttpClient, @Inject('API_URL') apiUrl: string, @Inject('API_VERSION') apiVersion: string) {
    super(httpClient, apiUrl, apiVersion, 'user/me');
  }

  // Customer
  getCustomerById(): Observable<CustomerResponse> {
    return this.getAll(`profile`).pipe(catchError((error) => of(error)));
  }

  updateCustomer(customer: UpdateCustomerInput): Observable<SubmitResponse> {
    return this.create(customer, `profile`).pipe(catchError((error) => of(error)));
  }
  updatePassword(customerInput: ChangePassword): Observable<SubmitResponse> {
    return this.create(customerInput, `change-password`).pipe(catchError((error) => of(error)));
  }

  // Address
  getAllAddresses(customerId: number): Observable<CustomerAddressResponse> {
    return this.getAll(`me/address/list/${customerId}`).pipe(catchError((error) => of(error)));
  }

  getAddressById(addressId: number): Observable<OneCustomerAddressResponse> {
    return this.getById(addressId, `me/address`).pipe(catchError((error) => of(error)));
  }

  createAddress(address: CreateCustomerAddressInput): Observable<SubmitResponse> {
    return this.create(address, `me/address`).pipe(catchError((error) => of(error)));
  }

  updateAddress(address: UpdateCustomerAddressInput): Observable<SubmitResponse> {
    return this.update(address, `me/address`).pipe(catchError((error) => of(error)));
  }

  setDefaultAddress(addressId: number): Observable<SubmitResponse> {
    return this.update(null, `me/set-default-address/${addressId}`).pipe(catchError((error) => of(error)));
  }

  deleteAddress(addressId: number): Observable<SubmitResponse> {
    return this.delete(addressId, `me/address`).pipe(catchError((error) => of(error)));
  }

  // Wishlist
  getWishlistByCustomer(customerId: number, page = 1): Observable<WishlistResponse> {
    return this.getAllPaged(`me/${customerId}/wishlist`, page).pipe(catchError((error) => of(error)));
  }

  getWishlistByCustomerAndCategory(customerId: number, categoryId: string, page = 1): Observable<WishlistByCategoryResponse> {
    return this.getAllPaged(`me/${customerId}/wishlist/category/${categoryId}`, page).pipe(catchError((error) => of(error)));
  }

  getWishlistCategoryByCustomer(customerId: number): Observable<WishlistCategoriesResponse> {
    return this.getAll(`me/${customerId}/wishlist/category/list`).pipe(catchError((error) => of(error)));
  }

  copyToWishlistCategory(input: MoveToWishlistCategoryInput, customerId: number): Observable<SubmitResponse> {
    return this.create(input, `me/${customerId}/wishlist/copy`).pipe(catchError((error) => of(error)));
  }

  moveToWishlistCategory(input: MoveToWishlistCategoryInput, customerId: number): Observable<SubmitResponse> {
    return this.update(input, `me/${customerId}/wishlist/move`).pipe(catchError((error) => of(error)));
  }
}
